import { Maximize2, Minimize2, Plus, X } from "lucide-react";
import { type ReactNode, useState } from "react";
import type { CopilotRuntimeProps } from "@/features/copilot/components/copilot-runtime";
import { HandMark } from "@/features/copilot/components/hand-mark";
import type { CopilotConversation } from "@/features/copilot/schemas/copilot.schema";
import { useCopilotLayout } from "@/shared/components/layout/copilot-layout.context";
import {
  COPILOT_PANEL_MAX_WIDTH,
  COPILOT_PANEL_MIN_WIDTH,
} from "@/shared/components/layout/copilot-width";
import { Button } from "@/shared/components/ui/button";
import { Skeleton } from "@/shared/components/ui/skeleton";

export function ResizeHandle() {
  const { width, setWidth } = useCopilotLayout();
  // The dock eases its width for expand and collapse; a drag must follow the pointer instead.
  const [resizing, setResizing] = useState(false);
  return (
    // reason: This focusable separator is a keyboard-operable resize control, not a decorative divider.
    // biome-ignore lint/a11y/useSemanticElements: reason: Expose keyboard resize through the separator role.
    <div
      role="separator"
      aria-label="Resize Hand panel"
      aria-orientation="vertical"
      aria-valuemin={COPILOT_PANEL_MIN_WIDTH}
      aria-valuemax={COPILOT_PANEL_MAX_WIDTH}
      aria-valuenow={width}
      tabIndex={0}
      className="absolute inset-y-0 left-0 z-10 w-1 cursor-col-resize touch-none transition-colors hover:bg-border focus-visible:bg-ring"
      data-resizing={resizing ? "" : undefined}
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        setResizing(true);
      }}
      onLostPointerCapture={() => setResizing(false)}
      onPointerMove={(event) => {
        if (event.buttons === 1) setWidth(window.innerWidth - event.clientX);
      }}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") setWidth(width + 16);
        if (event.key === "ArrowRight") setWidth(width - 16);
      }}
    />
  );
}

interface SurfaceProps {
  conversation: CopilotConversation | null;
  onNewConversation: () => void;
  loading: boolean;
  loadError: boolean;
  createError: boolean;
  scopeUnavailable: boolean;
  restoreError: boolean;
  runtimeProps: Pick<
    CopilotRuntimeProps,
    "scope" | "conversation" | "restoreRevision" | "onRestoreRequired" | "onStop"
  > | null;
  runtimeContent: ReactNode | null;
}

function SurfaceSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-4" role="status" aria-label="Loading Hand">
      <Skeleton className="h-10 w-2/3" />
      <Skeleton className="h-20 w-4/5 self-end" />
      <Skeleton className="h-16 w-3/4" />
    </div>
  );
}

export function CopilotPanelSurface({
  conversation,
  runtimeProps,
  runtimeContent,
  onNewConversation,
  loading,
  loadError,
  createError,
  scopeUnavailable,
  restoreError,
}: SurfaceProps) {
  const layout = useCopilotLayout();
  const close = () => {
    layout.close();
    window.requestAnimationFrame(() => layout.triggerRef.current?.focus());
  };
  const content =
    runtimeProps && runtimeContent ? (
      runtimeContent
    ) : (
      <div className="flex min-h-0 flex-1 items-center justify-center p-5 text-center">
        {loading ? <SurfaceSkeleton /> : null}
        {!loading && loadError ? (
          <p role="alert" className="text-sm text-destructive">
            Could not load your conversation. Close and reopen Hand to retry.
          </p>
        ) : null}
        {!loading && !loadError && createError ? (
          <div className="space-y-2 text-center">
            <p role="alert" className="text-sm text-destructive">
              Could not start a new conversation.
            </p>
            <Button type="button" variant="outline" onClick={onNewConversation}>
              Try again
            </Button>
          </div>
        ) : null}
        {!loading && !loadError && restoreError ? (
          <p role="alert" className="text-sm text-destructive">
            Could not restore the latest conversation. Close and reopen Hand to retry.
          </p>
        ) : null}
        {!loading && !loadError && !createError && scopeUnavailable ? (
          <p className="text-sm text-text-secondary">Select a workspace to use Hand.</p>
        ) : null}
        {!loading && !loadError && !createError && !restoreError && !scopeUnavailable ? (
          <p className="text-sm text-text-secondary">Preparing your workspace conversation…</p>
        ) : null}
      </div>
    );

  return (
    <section className="flex h-full min-h-0 flex-col" aria-label="Hand">
      <header className="flex min-h-14 items-center gap-2 border-b border-border px-4">
        <HandMark className="size-5 text-primary dark:text-(--pw-violet-300)" />
        <h2 className="min-w-0 flex-1 truncate font-semibold">Hand</h2>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="pointer-coarse:size-11"
          onClick={onNewConversation}
          aria-label="New chat"
          title="New chat"
        >
          <Plus aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="pointer-coarse:size-11"
          onClick={layout.toggleExpanded}
          aria-label={layout.isExpanded ? "Exit full page" : "Expand to full page"}
          title={layout.isExpanded ? "Exit full page" : "Expand to full page"}
        >
          {layout.isExpanded ? <Minimize2 aria-hidden="true" /> : <Maximize2 aria-hidden="true" />}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="pointer-coarse:size-11"
          onClick={close}
          aria-label="Close Hand"
          title="Close Hand"
        >
          <X aria-hidden="true" />
        </Button>
      </header>
      {content}
      {restoreError && conversation ? (
        <div
          role="alert"
          className="flex items-center gap-2 border-t border-border px-4 py-2 text-xs text-destructive"
        >
          <p className="min-w-0 flex-1">Could not restore the latest conversation.</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void runtimeProps?.onRestoreRequired().catch(() => undefined)}
          >
            Retry restore
          </Button>
        </div>
      ) : null}
    </section>
  );
}
